import { OvFormProvider, OvTabList, PageWrapper } from '@components'
import { CLIENT_DETAIL_TABS, LABELS, TOOLTIPS } from '@constants'
import { useSetPageTitle } from '@hooks/useSetPageTitle'
import { useStore } from '@hooks/useStore'
import { Delete, Edit, Save } from '@mui/icons-material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Button, Tab, Tooltip } from '@mui/material'
import { ISingleClient } from '@typings'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { clientValidationSchema } from 'src/partials/forms/client/validation/client.form.validation'

export const ClientDetailView = observer(() => {
  const { client, shared, ui } = useStore()
  const [readOnly, setReadOnly] = useState(true)
  const [selectedTab, setSelectedTab] = useState(CLIENT_DETAIL_TABS.EDIT[0].value)

  const { id } = useParams()

  if (!id) {
    return null
  }
  const _id = parseInt(id)

  const navigate = useNavigate()

  const getDefaultValues = async () => {
    return await client.getSingle(_id)
  }

  const handleCancel = () => {
    form.reset()
    setReadOnly(true)
  }

  const form = useForm({
    resolver: yupResolver(clientValidationSchema()),
    mode: 'onChange',
    defaultValues: getDefaultValues,
  })

  const handleTabChange = useCallback(
    async (e: React.SyntheticEvent<Element, Event>, val: string) => {
      // if (
      //   selectedTab === CLIENT_DETAIL_TABS[0].value &&
      //   form.formState.isDirty &&
      //   val === CLIENT_DETAIL_TABS[1].value
      // ) {
      //   await ui
      //     .confirm({
      //       title: 'Let op',
      //       content:
      //         'Er zijn wijzigingen die nog niet zijn opgeslagen. Wil je je wijzigingen bewaren?',
      //       confirmLabel: 'Ja, Bewaar',
      //       withCancel: true,
      //       cancelLabel: 'Nee',
      //     })
      //     .then(() => handleSubmit())
      //     .catch(() => {
      //       handleCancel()
      //     })
      // }
      setSelectedTab(val)
    },
    [form.formState.isDirty, selectedTab]
  )

  const onSubmitCallBack = (data: FieldValues) => {
    data.country_id = data.country.id
    client.update(data as ISingleClient).then(res => handleSucces(res))
  }

  const handleSucces = (data: ISingleClient) => {
    toast('Klantgegevens opggeslagen', { type: 'success' })
    setReadOnly(true)
    form.reset(data)
  }

  const handleSubmit = () => {
    form.handleSubmit(onSubmitCallBack)()
  }

  const handleDeleteClient = async () => {
    client.remove()
  }

  const renderClientTabs = useMemo(
    () => (
      <>
        <OvTabList onChange={handleTabChange}>
          {CLIENT_DETAIL_TABS.EDIT.map(singleTab => (
            <Tab
              key={singleTab.value}
              value={singleTab.value}
              label={singleTab.label}
            />
          ))}
        </OvTabList>
        <Box>
          {CLIENT_DETAIL_TABS.EDIT.map(singleTab => (
            <TabPanel
              key={singleTab.value}
              sx={{ padding: '24px 0' }}
              value={singleTab.value}>
              {singleTab.component}
            </TabPanel>
          ))}
        </Box>
      </>
    ),
    [selectedTab, client.current_client, client.single, client.loading]
  )

  useEffect(() => {
    // client.getClientContacts(_id)
    if (!shared.has_countries) shared.getCountries()

    return () => client.reset()
  }, [_id])

  return (
    <OvFormProvider
      form={form}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      readOnly={readOnly}
      loading={client.loading}>
      <PageWrapper
        pageTitle={client.single.name}
        backAction={() => navigate('..')}
        actions={
          <Box display="flex" gap={1} width={{ xs: '100%', sm: 'auto' }}>
            {!readOnly ? (
              <>
                <Tooltip title={TOOLTIPS.CANCEL_ESC}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancel}>
                    {LABELS.CANCEL}
                  </Button>
                </Tooltip>
                <Tooltip title={TOOLTIPS.SAVE_CLIENT}>
                  <Button
                    size="small"
                    endIcon={<Save />}
                    variant="contained"
                    onClick={handleSubmit}>
                    {LABELS.CLIENT_SAVE}
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title={TOOLTIPS.DELETE_CLIENT}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    endIcon={<Delete />}
                    onClick={handleDeleteClient}>
                    {LABELS.CLIENT_DELETE}
                  </Button>
                </Tooltip>
                <Tooltip title={TOOLTIPS.EDIT_CLIENT}>
                  <Button
                    size="small"
                    endIcon={<Edit />}
                    variant="contained"
                    onClick={() => setReadOnly(false)}>
                    {LABELS.CLIENT_EDIT}
                  </Button>
                </Tooltip>
              </>
            )}
          </Box>
        }
        fixedActions={!readOnly}>
        <TabContext value={selectedTab}> {renderClientTabs}</TabContext>
      </PageWrapper>
    </OvFormProvider>
  )
})
